import { useMemo, useState } from 'react';
import {
  Button,
  Alert,
  Stack,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Avatar,
  TablePagination,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useForm, Controller } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useListAssigneesQuery,
} from '../redux/firestoreApi';
import { showToast } from '../redux/uiSlice';
import {
  setSearch,
  setAssigneeFilter,
  setStatusFilter,
  setPriorityFilter,
  clearFilters,
} from '../redux/filtersSlice';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  TaskStatus,
  TaskPriority,
  Task,
  TASK_STATUS_META,
  TASK_PRIORITY_META,
} from '../types/models';

interface FormData {
  ticketNo: string;
  title: string;
  description?: string;
  assignee?: string;
  status: TaskStatus;
  priority: TaskPriority;
  storyPoints?: number;
}

export default function TasksPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const filters = useAppSelector((s) => s.filters);
  const dispatch = useAppDispatch();

  const { data: tasks = [], isLoading } = useListTasksQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const { data: assignees = [] } = useListAssigneesQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );

  const [create] = useCreateTaskMutation();
  const [update] = useUpdateTaskMutation();
  const [remove] = useDeleteTaskMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [toDelete, setToDelete] = useState<Task | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { register, handleSubmit, reset, control, formState: { errors } } =
    useForm<FormData>();

  const filtered = useMemo(() => {
    const needle = filters.search.toLowerCase();
    return tasks.filter((t) => {
      if (filters.status && t.status !== filters.status) return false;
      if (filters.priority && t.priority !== filters.priority) return false;
      if (filters.assignee && t.assignee !== filters.assignee) return false;
      if (needle) {
        const hay = `${t.ticketNo} ${t.title} ${t.description ?? ''} ${t.assignee ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [tasks, filters]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="Tasks" />
        <Alert severity="info">Select a project and sprint to manage tasks.</Alert>
      </>
    );
  }

  const openCreate = () => {
    setEditing(null);
    reset({
      ticketNo: '',
      title: '',
      description: '',
      assignee: '',
      status: 'IN_PROGRESS',
      priority: 'MEDIUM',
      storyPoints: undefined,
    });
    setOpen(true);
  };

  const openEdit = (t: Task) => {
    setEditing(t);
    reset({
      ticketNo: t.ticketNo,
      title: t.title,
      description: t.description ?? '',
      assignee: t.assignee ?? '',
      status: t.status,
      priority: t.priority,
      storyPoints: t.storyPoints,
    });
    setOpen(true);
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (editing) {
        await update({
          uid,
          projectId,
          sprintId,
          id: editing.id,
          patch: data,
        }).unwrap();
        dispatch(showToast({ message: 'Task updated', severity: 'success' }));
      } else {
        await create({ uid, projectId, sprintId, ...data }).unwrap();
        dispatch(showToast({ message: 'Task created', severity: 'success' }));
      }
      setOpen(false);
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Save failed', severity: 'error' }));
    }
  });

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await remove({ uid, projectId, sprintId, id: toDelete.id }).unwrap();
      dispatch(showToast({ message: 'Task deleted', severity: 'success' }));
    } catch (e: any) {
      dispatch(showToast({ message: e?.message ?? 'Delete failed', severity: 'error' }));
    }
  };

  const assigneeColor = (name?: string) =>
    assignees.find((a) => a.name === name)?.color ?? '#bdbdbd';

  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle={`${filtered.length} of ${tasks.length} tasks`}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New task
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
        >
          <TextField
            placeholder="Search tickets, titles, assignees…"
            value={filters.search}
            onChange={(e) => dispatch(setSearch(e.target.value))}
            sx={{ flexGrow: 1 }}
          />
          <TextField
            select
            label="Status"
            value={filters.status ?? ''}
            onChange={(e) =>
              dispatch(setStatusFilter((e.target.value as TaskStatus) || null))
            }
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {Object.entries(TASK_STATUS_META).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v.icon} {v.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Priority"
            value={filters.priority ?? ''}
            onChange={(e) =>
              dispatch(setPriorityFilter((e.target.value as TaskPriority) || null))
            }
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All</MenuItem>
            {Object.entries(TASK_PRIORITY_META).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                {v.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Assignee"
            value={filters.assignee ?? ''}
            onChange={(e) => dispatch(setAssigneeFilter(e.target.value || null))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {assignees.map((a) => (
              <MenuItem key={a.id} value={a.name}>
                {a.name}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Clear filters">
            <IconButton onClick={() => dispatch(clearFilters())}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {!isLoading && filtered.length === 0 ? (
        <EmptyState
          title="No tasks"
          description="Create a task or import from CSV."
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New task
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Assignee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell align="right">SP</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((t) => {
                const sm = TASK_STATUS_META[t.status];
                const pm = TASK_PRIORITY_META[t.priority];
                return (
                  <TableRow
                    key={t.id}
                    hover
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {t.ticketNo}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>{t.title}</TableCell>
                    <TableCell>
                      {t.assignee && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: assigneeColor(t.assignee),
                              fontSize: 12,
                            }}
                          >
                            {t.assignee[0]?.toUpperCase()}
                          </Avatar>
                          {t.assignee}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${sm.icon} ${sm.label}`}
                        sx={{ bgcolor: `${sm.color}22`, color: sm.color }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={pm.label}
                        sx={{ bgcolor: `${pm.color}22`, color: pm.color }}
                      />
                    </TableCell>
                    <TableCell align="right">{t.storyPoints ?? ''}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setToDelete(t)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit task' : 'New task'}</DialogTitle>
        <form onSubmit={onSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Ticket no."
                  placeholder="FRT-1234"
                  error={!!errors.ticketNo}
                  helperText={errors.ticketNo?.message}
                  {...register('ticketNo', { required: 'Required' })}
                />
                <TextField
                  label="Story points"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  {...register('storyPoints', { valueAsNumber: true })}
                />
              </Stack>
              <TextField
                label="Title"
                autoFocus
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title', { required: 'Required' })}
              />
              <TextField
                label="Description"
                multiline
                rows={3}
                {...register('description')}
              />
              <Stack direction="row" spacing={2}>
                <Controller
                  control={control}
                  name="assignee"
                  render={({ field }) => (
                    <TextField
                      select
                      label="Assignee"
                      sx={{ flexGrow: 1 }}
                      {...field}
                      value={field.value ?? ''}
                    >
                      <MenuItem value="">Unassigned</MenuItem>
                      {assignees.map((a) => (
                        <MenuItem key={a.id} value={a.name}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="status"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField select label="Status" {...field} sx={{ minWidth: 180 }}>
                      {Object.entries(TASK_STATUS_META).map(([k, v]) => (
                        <MenuItem key={k} value={k}>
                          {v.icon} {v.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="priority"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField select label="Priority" {...field} sx={{ minWidth: 140 }}>
                      {Object.entries(TASK_PRIORITY_META).map(([k, v]) => (
                        <MenuItem key={k} value={k}>
                          {v.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        title="Delete task?"
        message={`"${toDelete?.ticketNo} ${toDelete?.title}" will be removed.`}
        confirmLabel="Delete"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
